import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, FileText, Scale, AlertCircle, CheckCircle2, PenLine, ExternalLink } from 'lucide-react';

const LegalConsent = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [signature, setSignature] = useState('');
  const [consents, setConsents] = useState({
    cgu: false,
    cgv: false,
    nonRefundable: false
  });
  const [showCGU, setShowCGU] = useState(false);
  const [showCGV, setShowCGV] = useState(false);

  const TERMS_VERSION = '1.0.0'; // Update this when terms change

  const allChecked = consents.cgu && consents.cgv && consents.nonRefundable;
  const signatureValid = signature.trim().length >= 3;
  const canSubmit = allChecked && signatureValid;

  const handleConsent = (key) => {
    setConsents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/legal/accept-consent`,
        {
          signature: signature.trim(),
          terms_version: TERMS_VERSION,
          consents: {
            cgu_accepted: consents.cgu,
            cgv_accepted: consents.cgv,
            non_refundable_acknowledged: consents.nonRefundable
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Consentement enregistré');
      
      // Store flag to bypass consent check on next load
      localStorage.setItem('consent_just_accepted', 'true');
      
      // Force full page reload to refresh user state and bypass redirect loop
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Consent error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-3xl text-white mb-2">Conditions d'utilisation</h1>
          <p className="text-gray-400">
            Avant de continuer, merci de lire et accepter nos conditions.
          </p>
        </div>

        {/* Consent Card */}
        <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-[#1C1C1C] rounded-2xl p-8">
          {/* Checkboxes */}
          <div className="space-y-6 mb-8">
            {/* CGU */}
            <div className="flex items-start gap-4">
              <Checkbox
                id="cgu"
                checked={consents.cgu}
                onCheckedChange={() => handleConsent('cgu')}
                className="mt-1 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
              />
              <div className="flex-1">
                <label htmlFor="cgu" className="text-white cursor-pointer flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#D4AF37]" />
                  J'accepte les Conditions Générales d'Utilisation (CGU)
                </label>
                <button
                  onClick={() => navigate('/cgu')}
                  className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1 mt-1"
                >
                  Lire les CGU <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* CGV */}
            <div className="flex items-start gap-4">
              <Checkbox
                id="cgv"
                checked={consents.cgv}
                onCheckedChange={() => handleConsent('cgv')}
                className="mt-1 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
              />
              <div className="flex-1">
                <label htmlFor="cgv" className="text-white cursor-pointer flex items-center gap-2">
                  <Scale className="w-4 h-4 text-[#D4AF37]" />
                  J'accepte les Conditions Générales de Vente (CGV)
                </label>
                <button
                  onClick={() => navigate('/cgv')}
                  className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1 mt-1"
                >
                  Lire les CGV <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Non-refundable */}
            <div className="flex items-start gap-4">
              <Checkbox
                id="nonRefundable"
                checked={consents.nonRefundable}
                onCheckedChange={() => handleConsent('nonRefundable')}
                className="mt-1 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
              />
              <div className="flex-1">
                <label htmlFor="nonRefundable" className="text-white cursor-pointer flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#D4AF37]" />
                  Je comprends que tous les achats sont non remboursables
                </label>
                <p className="text-gray-500 text-sm mt-1">
                  Conformément à nos CGV, les services numériques ne sont pas remboursables après achat.
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#1C1C1C] my-8" />

          {/* Electronic Signature */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-white mb-3">
              <PenLine className="w-4 h-4 text-[#D4AF37]" />
              Signature électronique
            </label>
            <p className="text-gray-500 text-sm mb-4">
              En tapant votre nom complet ci-dessous, vous confirmez avoir lu et accepté les conditions ci-dessus.
            </p>
            <Input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Votre nom complet"
              className="bg-[#0A0A0A] border-[#1C1C1C] text-white placeholder:text-gray-600 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 text-lg py-6 font-serif"
              data-testid="signature-input"
            />
            {signature && (
              <p className="text-[#D4AF37] text-sm mt-2 font-serif italic">
                Signé électroniquement par : {signature}
              </p>
            )}
          </div>

          {/* Validation Status */}
          <div className="flex items-center gap-2 mb-6">
            {canSubmit ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-green-400 text-sm">Tous les champs sont validés</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-orange-500" />
                <span className="text-orange-400 text-sm">
                  {!allChecked ? 'Veuillez cocher toutes les cases' : 'Veuillez signer avec votre nom complet'}
                </span>
              </>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="w-full btn-gold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="consent-submit-btn"
          >
            {loading ? 'Enregistrement...' : 'Confirmer et continuer'}
          </Button>

          {/* Legal note */}
          <p className="text-gray-600 text-xs text-center mt-6">
            Votre consentement sera enregistré de manière sécurisée avec la date, l'heure et votre signature électronique.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LegalConsent;
